import { useMemo, useState } from 'react';
import {
  Alert,
  alpha,
  Box,
  Chip,
  Paper,
  Stack,
  Switch,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  TextField,
  ToggleButton,
  ToggleButtonGroup,
  Typography,
} from '@mui/material';
import CheckRoundedIcon from '@mui/icons-material/CheckRounded';
import CloseRoundedIcon from '@mui/icons-material/CloseRounded';
import { useMutation, useQueries, useQueryClient } from '@tanstack/react-query';
import { useSnackbar } from 'notistack';
import {
  createIndustrialCostFinalAllocationRule,
  createIndustrialCostStageProductApplicability,
  deleteIndustrialCostFinalAllocationRule,
  deleteIndustrialCostStageProductApplicability,
  getIndustrialCostFinalAllocationRules,
  getIndustrialCostProducts,
  getIndustrialCostStageProductApplicabilities,
  getIndustrialCostStages,
} from '../services/industrialCostService';
import IndustrialCostSectionPlaceholder from '../components/IndustrialCostSectionPlaceholder';
import type {
  CreateFinalAllocationRulePayload,
  CreateStageProductApplicabilityPayload,
  FinalAllocationDriverType,
  FinalAllocationRule,
  Product,
  Stage,
  StageProductApplicability,
} from '../types/industrialCost';

function getErrorMessage(error: unknown): string {
  if (typeof error === 'object' && error !== null) {
    const maybeResponse = error as {
      response?: { data?: { detail?: string } };
      message?: string;
    };

    if (maybeResponse.response?.data?.detail) {
      return maybeResponse.response.data.detail;
    }

    if (maybeResponse.message) {
      return maybeResponse.message;
    }
  }

  return 'Se produjo un error inesperado.';
}

type CompactMetricCardProps = {
  label: string;
  value: string;
};

function CompactMetricCard({ label, value }: CompactMetricCardProps) {
  return (
    <Paper
      elevation={0}
      sx={{
        borderRadius: 3,
        border: '1px solid rgba(148,163,184,0.16)',
        bgcolor: 'rgba(15,23,42,0.5)',
        px: 2,
        py: 1.4,
      }}
    >
      <Stack spacing={0.35}>
        <Typography
          variant="caption"
          sx={{
            color: '#94a3b8',
            fontWeight: 800,
            letterSpacing: 0.6,
            textTransform: 'uppercase',
            lineHeight: 1.1,
          }}
        >
          {label}
        </Typography>
        <Typography
          variant="h6"
          sx={{
            color: '#f8fafc',
            fontWeight: 900,
            lineHeight: 1.1,
          }}
        >
          {value}
        </Typography>
      </Stack>
    </Paper>
  );
}

function getStageVisual(index: number) {
  const palette = [
    {
      headerBg: 'linear-gradient(180deg, rgba(56,189,248,0.1) 0%, rgba(15,23,42,0.98) 100%)',
      cellBg: 'rgba(56,189,248,0.04)',
      cellActiveBg: 'rgba(56,189,248,0.12)',
      cellInactiveBg: 'rgba(127,29,29,0.12)',
      border: 'rgba(56,189,248,0.24)',
      strong: '#7dd3fc',
      soft: '#bae6fd',
    },
    {
      headerBg: 'linear-gradient(180deg, rgba(59,130,246,0.1) 0%, rgba(15,23,42,0.98) 100%)',
      cellBg: 'rgba(59,130,246,0.04)',
      cellActiveBg: 'rgba(59,130,246,0.12)',
      cellInactiveBg: 'rgba(127,29,29,0.12)',
      border: 'rgba(59,130,246,0.24)',
      strong: '#93c5fd',
      soft: '#bfdbfe',
    },
    {
      headerBg: 'linear-gradient(180deg, rgba(99,102,241,0.1) 0%, rgba(15,23,42,0.98) 100%)',
      cellBg: 'rgba(99,102,241,0.04)',
      cellActiveBg: 'rgba(99,102,241,0.12)',
      cellInactiveBg: 'rgba(127,29,29,0.12)',
      border: 'rgba(99,102,241,0.24)',
      strong: '#a5b4fc',
      soft: '#c7d2fe',
    },
    {
      headerBg: 'linear-gradient(180deg, rgba(168,85,247,0.1) 0%, rgba(15,23,42,0.98) 100%)',
      cellBg: 'rgba(168,85,247,0.04)',
      cellActiveBg: 'rgba(168,85,247,0.12)',
      cellInactiveBg: 'rgba(127,29,29,0.12)',
      border: 'rgba(168,85,247,0.24)',
      strong: '#d8b4fe',
      soft: '#e9d5ff',
    },
  ];

  return palette[index % palette.length];
}

export default function IndustrialCostFinalAllocationRulesView() {
  const queryClient = useQueryClient();
  const { enqueueSnackbar } = useSnackbar();
  const [search, setSearch] = useState('');

  const [stagesQuery, productsQuery, rulesQuery, applicabilitiesQuery] = useQueries({
    queries: [
      {
        queryKey: ['industrial-cost-stages'],
        queryFn: getIndustrialCostStages,
      },
      {
        queryKey: ['industrial-cost-products'],
        queryFn: getIndustrialCostProducts,
      },
      {
        queryKey: ['industrial-cost-final-allocation-rules'],
        queryFn: getIndustrialCostFinalAllocationRules,
      },
      {
        queryKey: ['industrial-cost-stage-product-applicabilities'],
        queryFn: getIndustrialCostStageProductApplicabilities,
      },
    ],
  });

  const stages = stagesQuery.data ?? [];
  const products = productsQuery.data ?? [];
  const rules = rulesQuery.data ?? [];
  const applicabilities = applicabilitiesQuery.data ?? [];

  const primaryStages = useMemo(
    () =>
      [...stages]
        .filter((stage: Stage) => stage.stage_type === 'primary' && stage.is_active)
        .sort((a, b) => a.display_order - b.display_order),
    [stages],
  );

  const activeProducts = useMemo(
    () =>
      [...products]
        .filter((product: Product) => product.is_active)
        .sort((a, b) => a.name.localeCompare(b.name)),
    [products],
  );

  const rulesByStageId = useMemo(
    () => new Map<string, FinalAllocationRule>(rules.map((rule) => [rule.stage_id, rule])),
    [rules],
  );

  const applicabilityByStageAndProduct = useMemo(() => {
    const map = new Map<string, StageProductApplicability>();

    applicabilities.forEach((item) => {
      const key = `${item.stage_id}__${item.product_id}`;
      map.set(key, item);
    });

    return map;
  }, [applicabilities]);

  const summary = useMemo(() => {
    const stagesWithDriver = primaryStages.filter((stage) => rulesByStageId.has(stage.id)).length;
    const activeLinks = applicabilities.filter((item) => item.is_active).length;

    return {
      primaryStageCount: primaryStages.length,
      productsCount: activeProducts.length,
      stagesWithDriver,
      stagesWithoutDriver: Math.max(primaryStages.length - stagesWithDriver, 0),
      activeLinks,
    };
  }, [primaryStages, activeProducts, rulesByStageId, applicabilities]);

  const filteredProducts = useMemo(() => {
    const normalized = search.trim().toLowerCase();

    if (!normalized) {
      return activeProducts;
    }

    return activeProducts.filter((product) => {
      const haystack = [
        product.name,
        product.code,
        product.family ?? '',
        product.format ?? '',
      ]
        .join(' ')
        .toLowerCase();

      return haystack.includes(normalized);
    });
  }, [activeProducts, search]);

  const driverMutation = useMutation({
    mutationFn: async ({
      stage,
      nextDriver,
      existingRule,
    }: {
      stage: Stage;
      nextDriver: FinalAllocationDriverType;
      existingRule: FinalAllocationRule | null;
    }) => {
      if (existingRule) {
        await deleteIndustrialCostFinalAllocationRule(existingRule.id);
      }

      const payload: CreateFinalAllocationRulePayload = {
        stage_id: stage.id,
        driver_type: nextDriver,
        is_active: true,
        notes: null,
      };

      await createIndustrialCostFinalAllocationRule(payload);
    },
    onSuccess: async () => {
      enqueueSnackbar('Driver de etapa actualizado correctamente.', {
        variant: 'success',
      });
      await queryClient.invalidateQueries({
        queryKey: ['industrial-cost-final-allocation-rules'],
      });
    },
    onError: (mutationError) => {
      enqueueSnackbar(getErrorMessage(mutationError), { variant: 'error' });
    },
  });

  const applicabilityMutation = useMutation({
    mutationFn: async ({
      stageId,
      productId,
      existing,
      checked,
    }: {
      stageId: string;
      productId: string;
      existing: StageProductApplicability | null;
      checked: boolean;
    }) => {
      if (checked) {
        if (existing) {
          return;
        }

        const payload: CreateStageProductApplicabilityPayload = {
          stage_id: stageId,
          product_id: productId,
          is_active: true,
          notes: null,
        };
        await createIndustrialCostStageProductApplicability(payload);
        return;
      }

      if (existing) {
        await deleteIndustrialCostStageProductApplicability(existing.id);
      }
    },
    onSuccess: async () => {
      await queryClient.invalidateQueries({
        queryKey: ['industrial-cost-stage-product-applicabilities'],
      });
    },
    onError: (mutationError) => {
      enqueueSnackbar(getErrorMessage(mutationError), { variant: 'error' });
    },
  });

  const isLoading =
    stagesQuery.isLoading ||
    productsQuery.isLoading ||
    rulesQuery.isLoading ||
    applicabilitiesQuery.isLoading;

  const isError =
    stagesQuery.isError ||
    productsQuery.isError ||
    rulesQuery.isError ||
    applicabilitiesQuery.isError;

  const error =
    stagesQuery.error ||
    productsQuery.error ||
    rulesQuery.error ||
    applicabilitiesQuery.error;

  const isBusy = driverMutation.isPending || applicabilityMutation.isPending;

  return (
    <Stack spacing={2}>
      <Paper
        elevation={0}
        sx={{
          borderRadius: 4,
          border: '1px solid rgba(148,163,184,0.16)',
          bgcolor: 'rgba(15,23,42,0.62)',
          backdropFilter: 'blur(10px)',
          px: { xs: 2.25, md: 2.75 },
          py: { xs: 2, md: 2.25 },
        }}
      >
        <Stack spacing={0.8}>
          <Typography variant="h6" sx={{ color: '#f8fafc', fontWeight: 900 }}>
            Reglas finales
          </Typography>

          <Typography variant="body2" sx={{ color: '#94a3b8', lineHeight: 1.6 }}>
            Define por qué etapas principales pasa cada producto y qué driver utiliza cada etapa.
          </Typography>
        </Stack>
      </Paper>

      {isError ? (
        <Alert severity="error">{getErrorMessage(error)}</Alert>
      ) : isLoading ? (
        <Paper
          elevation={0}
          sx={{
            borderRadius: 4,
            border: '1px solid rgba(148,163,184,0.16)',
            bgcolor: 'rgba(15,23,42,0.62)',
            px: 2.5,
            py: 2.5,
          }}
        >
          <Typography variant="body2" sx={{ color: '#94a3b8' }}>
            Cargando reglas finales...
          </Typography>
        </Paper>
      ) : primaryStages.length === 0 ? (
        <Paper
          elevation={0}
          sx={{
            borderRadius: 4,
            border: '1px solid rgba(148,163,184,0.16)',
            bgcolor: 'rgba(15,23,42,0.62)',
            px: 2.5,
            py: 2.5,
          }}
        >
          <IndustrialCostSectionPlaceholder
            title="Sin etapas principales activas"
            description="Necesitas etapas principales activas para configurar el recorrido final por producto."
          />
        </Paper>
      ) : activeProducts.length === 0 ? (
        <Paper
          elevation={0}
          sx={{
            borderRadius: 4,
            border: '1px solid rgba(148,163,184,0.16)',
            bgcolor: 'rgba(15,23,42,0.62)',
            px: 2.5,
            py: 2.5,
          }}
        >
          <IndustrialCostSectionPlaceholder
            title="Sin productos activos"
            description="Necesitas productos activos para configurar por qué etapas principales pasa cada uno."
          />
        </Paper>
      ) : (
        <>
          <Box
            sx={{
              display: 'grid',
              gridTemplateColumns: {
                xs: 'repeat(2, minmax(0, 1fr))',
                lg: 'repeat(4, minmax(0, 1fr))',
              },
              gap: 1.5,
            }}
          >
            <CompactMetricCard
              label="Etapas principales"
              value={String(summary.primaryStageCount)}
            />
            <CompactMetricCard
              label="Productos activos"
              value={String(summary.productsCount)}
            />
            <CompactMetricCard
              label="Etapas sin driver"
              value={String(summary.stagesWithoutDriver)}
            />
            <CompactMetricCard
              label="Relaciones activas"
              value={String(summary.activeLinks)}
            />
          </Box>

          <Paper
            elevation={0}
            sx={{
              borderRadius: 4,
              border: '1px solid rgba(148,163,184,0.16)',
              bgcolor: 'rgba(15,23,42,0.66)',
              backdropFilter: 'blur(10px)',
              overflow: 'hidden',
            }}
          >
            <Box
              sx={{
                px: { xs: 2, md: 2.5 },
                py: 1.75,
                borderBottom: '1px solid rgba(148,163,184,0.12)',
                display: 'flex',
                flexDirection: { xs: 'column', lg: 'row' },
                alignItems: { xs: 'stretch', lg: 'center' },
                justifyContent: 'space-between',
                gap: 1.5,
              }}
            >
              <Typography variant="subtitle1" sx={{ color: '#f8fafc', fontWeight: 800 }}>
                Flujo por producto
              </Typography>

              <TextField
                label="Buscar producto"
                placeholder="Nombre, código, familia o formato"
                value={search}
                onChange={(e) => setSearch(e.target.value)}
                size="small"
                sx={{
                  width: { xs: '100%', lg: 360 },
                  '& .MuiInputLabel-root': {
                    color: '#94a3b8',
                  },
                  '& .MuiOutlinedInput-root': {
                    color: '#f8fafc',
                    bgcolor: 'rgba(2,6,23,0.34)',
                    '& fieldset': {
                      borderColor: 'rgba(148,163,184,0.2)',
                    },
                    '&:hover fieldset': {
                      borderColor: 'rgba(148,163,184,0.3)',
                    },
                    '&.Mui-focused fieldset': {
                      borderColor: 'rgba(56,189,248,0.48)',
                    },
                  },
                }}
              />
            </Box>

            {filteredProducts.length === 0 ? (
              <Box sx={{ p: 2.5 }}>
                <IndustrialCostSectionPlaceholder
                  title="Sin productos para el filtro actual"
                  description="Ajusta la búsqueda para volver a mostrar productos en la matriz."
                />
              </Box>
            ) : (
              <Box sx={{ overflowX: 'auto' }}>
                <Table sx={{ minWidth: 920 }}>
                  <TableHead>
                    <TableRow>
                      <TableCell
                        sx={{
                          color: '#94a3b8',
                          fontWeight: 800,
                          minWidth: 250,
                          position: 'sticky',
                          left: 0,
                          zIndex: 4,
                          bgcolor: 'rgba(15,23,42,0.98)',
                          backdropFilter: 'blur(10px)',
                          py: 1.4,
                        }}
                      >
                        Producto
                      </TableCell>

                      {primaryStages.map((stage, index) => {
                        const rule = rulesByStageId.get(stage.id) ?? null;
                        const selectedDriver = rule?.driver_type ?? 'kg';
                        const visual = getStageVisual(index);

                        return (
                          <TableCell
                            key={stage.id}
                            sx={{
                              minWidth: 180,
                              verticalAlign: 'top',
                              p: 0,
                              borderLeft: `1px solid ${visual.border}`,
                              background: visual.headerBg,
                            }}
                          >
                            <Box sx={{ px: 1.5, py: 1.15 }}>
                              <Stack spacing={1}>
                                <Stack direction="row" alignItems="center" justifyContent="space-between" spacing={1}>
                                  <Typography
                                    sx={{
                                      color: '#f8fafc',
                                      fontWeight: 800,
                                      lineHeight: 1.2,
                                    }}
                                  >
                                    {stage.name}
                                  </Typography>

                                  <Box
                                    sx={{
                                      width: 10,
                                      height: 10,
                                      borderRadius: '999px',
                                      bgcolor: visual.strong,
                                      boxShadow: `0 0 0 3px ${alpha(visual.strong, 0.12)}`,
                                      flexShrink: 0,
                                    }}
                                  />
                                </Stack>

                                <ToggleButtonGroup
                                  exclusive
                                  size="small"
                                  value={selectedDriver}
                                  disabled={driverMutation.isPending}
                                  onChange={(_, nextValue) => {
                                    if (!nextValue) return;

                                    driverMutation.mutate({
                                      stage,
                                      nextDriver: nextValue as FinalAllocationDriverType,
                                      existingRule: rule,
                                    });
                                  }}
                                  sx={{
                                    alignSelf: 'flex-start',
                                    '& .MuiToggleButtonGroup-grouped': {
                                      borderColor: alpha(visual.strong, 0.24),
                                      color: '#cbd5e1',
                                      px: 1.2,
                                      py: 0.25,
                                      minHeight: 28,
                                      fontSize: '0.74rem',
                                      fontWeight: 800,
                                      textTransform: 'none',
                                      bgcolor: 'rgba(2,6,23,0.2)',
                                      '&.Mui-selected': {
                                        color: '#f8fafc',
                                        bgcolor: alpha(visual.strong, 0.16),
                                      },
                                    },
                                  }}
                                >
                                  <ToggleButton value="kg">kg</ToggleButton>
                                  <ToggleButton value="units">units</ToggleButton>
                                </ToggleButtonGroup>
                              </Stack>
                            </Box>
                          </TableCell>
                        );
                      })}
                    </TableRow>
                  </TableHead>

                  <TableBody>
                    {filteredProducts.map((product) => (
                      <TableRow key={product.id} hover>
                        <TableCell
                          sx={{
                            minWidth: 250,
                            position: 'sticky',
                            left: 0,
                            zIndex: 3,
                            bgcolor: 'rgba(15,23,42,0.96)',
                            backdropFilter: 'blur(10px)',
                            py: 1.5,
                          }}
                        >
                          <Stack spacing={0.25}>
                            <Typography sx={{ color: '#f8fafc', fontWeight: 800 }}>
                              {product.name}
                            </Typography>
                            <Typography variant="body2" sx={{ color: '#94a3b8' }}>
                              {product.code}
                              {product.format ? ` · ${product.format}` : ''}
                            </Typography>
                          </Stack>
                        </TableCell>

                        {primaryStages.map((stage, index) => {
                          const key = `${stage.id}__${product.id}`;
                          const applicability = applicabilityByStageAndProduct.get(key) ?? null;
                          const checked = Boolean(applicability?.is_active);
                          const visual = getStageVisual(index);

                          return (
                            <TableCell
                              key={key}
                              sx={{
                                borderLeft: `1px solid ${visual.border}`,
                                bgcolor: checked ? visual.cellActiveBg : visual.cellInactiveBg,
                                transition: 'background-color 140ms ease',
                                py: 1.1,
                              }}
                            >
                              <Stack
                                direction="row"
                                spacing={1}
                                justifyContent="center"
                                alignItems="center"
                                sx={{ minHeight: 44 }}
                              >
                                <Switch
                                  checked={checked}
                                  disabled={isBusy}
                                  onChange={(e) =>
                                    applicabilityMutation.mutate({
                                      stageId: stage.id,
                                      productId: product.id,
                                      existing: applicability,
                                      checked: e.target.checked,
                                    })
                                  }
                                  sx={{
                                    mr: 0.25,
                                    '& .MuiSwitch-switchBase': {
                                      color: checked ? visual.strong : '#fca5a5',
                                    },
                                    '& .MuiSwitch-switchBase.Mui-checked': {
                                      color: '#22c55e',
                                    },
                                    '& .MuiSwitch-switchBase.Mui-checked + .MuiSwitch-track': {
                                      bgcolor: '#22c55e',
                                    },
                                    '& .MuiSwitch-track': {
                                      bgcolor: checked ? alpha('#22c55e', 0.5) : alpha('#ef4444', 0.5),
                                    },
                                  }}
                                />

                                {checked ? (
                                  <Chip
                                    size="small"
                                    icon={<CheckRoundedIcon sx={{ color: '#86efac !important' }} />}
                                    label=""
                                    sx={{
                                      height: 26,
                                      minWidth: 34,
                                      bgcolor: alpha('#22c55e', 0.12),
                                      border: `1px solid ${alpha('#22c55e', 0.28)}`,
                                      '& .MuiChip-label': {
                                        px: 0,
                                      },
                                    }}
                                  />
                                ) : (
                                  <Chip
                                    size="small"
                                    icon={<CloseRoundedIcon sx={{ color: '#fca5a5 !important' }} />}
                                    label=""
                                    sx={{
                                      height: 26,
                                      minWidth: 34,
                                      bgcolor: alpha('#ef4444', 0.1),
                                      border: `1px solid ${alpha('#ef4444', 0.24)}`,
                                      '& .MuiChip-label': {
                                        px: 0,
                                      },
                                    }}
                                  />
                                )}
                              </Stack>
                            </TableCell>
                          );
                        })}
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </Box>
            )}
          </Paper>
        </>
      )}
    </Stack>
  );
}